import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { practiceFeatures } from "../../config/practice";
import SideNavLayout from "../../customsidenav/sidenavlayout";
import { formwizApi } from "./formwizApi";
import { getDef } from "./defsCache";
import { eventBus } from "../../utils/eventBus";
import { useSendFormsWizard } from "./launch";
import { useStaffFormFill } from "../internal/launch";
import { internalApi } from "../internal/internalApi";
import "../internal/internal.css";
import useInboxCount from "./useInboxCount";
import SubmissionReview from "./SubmissionReview";
import {
  EMR_STATUS,
  emrStatusOf,
  bandSeverity,
  errInfo,
  fmtDate,
  fmtDateTime,
  fmtScore,
  sevTone,
  statusInfo,
} from "./staffUtils";
import "./staff.css";
import { holdsScreen } from "../../config/screenPrivileges";

export const INBOX_VIEWS = [
  { id: "needs_review", label: "Needs review" },
  { id: "flagged", label: "Flagged" },
  { id: "emr_failed", label: "EMR failed" },
  { id: "reviewed", label: "Reviewed" },
  { id: "all", label: "All" },
];
// Internal staff forms (practiceFeatures().staffForms): their own tab.
export const STAFF_VIEW = { id: "staff", label: "Staff forms" };
export const inboxViews = (staffForms) => (staffForms ? [...INBOX_VIEWS.slice(0, 4), STAFF_VIEW, INBOX_VIEWS[4]] : INBOX_VIEWS);
const PAGE_SIZE = 25;

// GET /inbox query params (empty filters left out).
export function inboxParams({ view, from, to, q, page }) {
  const p = { view: view || "needs_review", page: page || 1, page_size: PAGE_SIZE };
  if (from) p.from = from;
  if (to) p.to = to;
  if (q && q.trim()) p.q = q.trim();
  return p;
}

function ScoreChip({ item, sev }) {
  if (item.primary_score === null || item.primary_score === undefined) return <span className="fws-muted">—</span>;
  return (
    <span className={`fws-chip has-dot fws-tone-${sev ? sevTone(sev) : "muted"}`} data-testid={`score-${item.id}`}>
      {fmtScore(item.primary_score)}{item.primary_band ? ` · ${item.primary_band}` : ""}
    </span>
  );
}

function FlagsCell({ item }) {
  const flags = Array.isArray(item.flags) ? item.flags : [];
  if (!item.flag_severity || item.flag_severity === "none") return <span className="fws-muted">—</span>;
  if (item.flag_severity === "critical") {
    return <span className="fws-chip is-critical" title={flags.map((f) => (f.label && f.label.en) || f.label || f.id).join(", ")}>Critical</span>;
  }
  return <span className={`fws-chip fws-tone-${sevTone(item.flag_severity)}`}>{flags.length > 1 ? `${flags.length} flags` : "Flag"}</span>;
}

/*
 * Above the Staff forms table: staff-form drafts and saved forms with
 * unsaved edits (mine, or everyone's), each with Continue.
 */
function DraftsStrip({ onContinue, version }) {
  const [mine, setMine] = useState(true);
  const [items, setItems] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | unavailable | error
  useEffect(() => {
    let alive = true;
    setState("loading");
    internalApi.drafts(mine ? { mine: 1 } : {})
      .then((r) => {
        if (!alive) return;
        setItems(Array.isArray(r && r.items) ? r.items : []);
        setState("ready");
      })
      .catch((e) => {
        if (!alive) return;
        const st = errInfo(e).status;
        setState(st === 404 || st === 503 ? "unavailable" : "error");
      });
    return () => {
      alive = false;
    };
  }, [mine, version]);
  if (state === "unavailable") return <div className="fws-alert fws-alert-info" style={{ marginBottom: 12 }} data-testid="fwi-drafts-unavailable">Staff forms aren't available yet.</div>;
  return (
    <div className="fwi-strip" data-testid="fwi-drafts-strip">
      <div className="fwi-strip-head">
        <h3>Drafts and unsaved edits</h3>
        <div className="fws-seg" role="group" aria-label="Whose drafts">
          <button type="button" aria-pressed={mine} onClick={() => setMine(true)}>Mine</button>
          <button type="button" aria-pressed={!mine} onClick={() => setMine(false)}>Everyone</button>
        </div>
      </div>
      {state === "loading" && <p className="fws-muted fws-small">Loading…</p>}
      {state === "error" && <p className="fws-err">Couldn't load the drafts.</p>}
      {state === "ready" && items.length === 0 && <p className="fws-muted fws-small">No open drafts.</p>}
      {state === "ready" && items.map((it) => {
        const p = it.patient || {};
        return (
          <div key={it.id} className="fws-sub" style={{ cursor: "default" }} data-testid={`fwi-draft-${it.id}`}>
            <span className="fws-sub-title">{it.form_title || it.form_id}</span>
            <span className="fws-chip fws-tone-info">{it.status === "in_progress" ? "Draft" : "Unsaved edit"}</span>
            <span className="fws-small">{[p.firstname, p.lastname].filter(Boolean).join(" ") || `Patient ${it.patientid || p.patientid || ""}`}{p.dob ? ` · DOB ${fmtDate(p.dob)}` : ""}</span>
            <span className="fws-muted fws-small">{it.created_by ? `by ${it.created_by}` : ""}{it.edited_at || it.updated_at ? ` · ${fmtDateTime(it.edited_at || it.updated_at)}` : ""}</span>
            <span className="fws-spacer" />
            <button type="button" className="fws-btn fws-btn-sm fws-btn-primary" onClick={() => onContinue(it)}>Continue</button>
          </div>
        );
      })}
    </div>
  );
}

function Unavailable() {
  return <div className="fws fws-page"><p style={{ padding: 24 }}>Page not available</p></div>;
}

/*
 * Form Inbox (/form-inbox): submitted forms waiting for review, flagged ones,
 * EMR failures, reviewed and all. A row opens the submission review.
 */
// Exported for the unified Inbox (src/inbox/InboxPage.jsx, /inbox/forms), which
// renders it inside its own layout.
export function FormInbox() {
  const navigate = useNavigate();
  const privileges = useSelector((state) => (state.userPrivileges && state.userPrivileges.privileges) || []);
  const [view, setView] = useState("needs_review");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [sevByItem, setSevByItem] = useState({});
  const [reviewId, setReviewId] = useState(null);
  const [openWizard, wizard] = useSendFormsWizard();
  const [openStaffForm, staffFormFill] = useStaffFormFill();
  const staffForms = !!practiceFeatures().staffForms;
  const views = inboxViews(staffForms);
  const [staffNote, setStaffNote] = useState("");
  const [draftsVersion, setDraftsVersion] = useState(0);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    setStaffNote("");
    try {
      const res = await formwizApi.inbox(inboxParams({ view, from, to, q, page }));
      if (mine !== seq.current) return;
      setList({ items: Array.isArray(res && res.items) ? res.items : [], total: Number(res && res.total) || 0 });
    } catch (e) {
      if (mine !== seq.current) return;
      const info = errInfo(e);
      // A server without staff forms refuses view=staff: only that tab is empty.
      if (view === "staff" && (info.status === 404 || info.status === 422)) setStaffNote("Staff forms aren't available yet.");
      else if (info.status === 404) setUnavailable(true);
      else setError(info.status === 403 ? "You don't have access to the Form Inbox." : "Couldn't load the inbox. Try again.");
      setList({ items: [], total: 0 });
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [view, from, to, q, page]);

  // Tab counts: the side nav's badge source (shared cache, one request).
  const counts = useInboxCount(true);
  const loadCounts = useCallback(() => eventBus.emit("formwiz:inbox-changed"), []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const h = () => {
      load();
      loadCounts();
    };
    const hs = () => {
      load();
      setDraftsVersion((n) => n + 1);
    };
    eventBus.on("formwiz:requests-changed", h);
    eventBus.on("formwiz:staff-forms-changed", hs);
    return () => {
      eventBus.off("formwiz:requests-changed", h);
      eventBus.off("formwiz:staff-forms-changed", hs);
    };
  }, [load, loadCounts]);

  // Band severity for the score chips: from each form's definition (cached).
  useEffect(() => {
    let alive = true;
    list.items.forEach((it) => {
      if (it.primary_score === null || it.primary_score === undefined || sevByItem[it.id] !== undefined) return;
      getDef(it.form_id, it.form_version)
        .then((def) => {
          if (alive) setSevByItem((m) => ({ ...m, [it.id]: bandSeverity(def, it.primary_score, it.primary_band) }));
        })
        .catch(() => {
          if (alive) setSevByItem((m) => ({ ...m, [it.id]: null }));
        });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.items]);

  if (unavailable) {
    return (
      <div className="fws fws-page">
        <div className="fws-page-head"><h1>Form Inbox</h1></div>
        <div className="fws-alert fws-alert-info">Form Wizard isn't available for this practice yet.</div>
      </div>
    );
  }

  const pages = Math.max(1, Math.ceil(list.total / PAGE_SIZE));
  const firstRow = list.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastRow = Math.min(page * PAGE_SIZE, list.total);
  const pickView = (v) => {
    setView(v);
    setPage(1);
  };
  const tabCount = (id) => {
    if (!counts) return null;
    if (id === "needs_review") return Number(counts.unreviewed) || 0;
    if (id === "emr_failed") return Number(counts.emr_failed) || 0;
    return null;
  };

  return (
    <div className="fws fws-page" data-testid="form-inbox">
      <div className="fws-page-head">
        <h1>Form Inbox</h1>
        {counts && Number(counts.critical_unreviewed) > 0 && (
          <span className="fws-chip is-critical">{counts.critical_unreviewed} critical to review</span>
        )}
        <span className="fws-spacer" />
        {staffForms && (
          <button type="button" className="fws-btn" onClick={() => openStaffForm(null, { onDone: () => { load(); setDraftsVersion((n) => n + 1); } })}>Fill staff form</button>
        )}
        <button type="button" className="fws-btn fws-btn-accent" onClick={() => openWizard(null)}>Send forms</button>
        {holdsScreen(privileges, "formSettings") && (
          <button type="button" className="fws-icon-btn" aria-label="Form settings" title="Form settings" onClick={() => navigate("/form-settings")}>
            <SettingsOutlinedIcon />
          </button>
        )}
      </div>

      <div className="fws-tabs" role="tablist" aria-label="Inbox views">
        {views.map((v) => {
          const n = tabCount(v.id);
          return (
            <button key={v.id} type="button" role="tab" className="fws-tab" aria-selected={view === v.id} onClick={() => pickView(v.id)}>
              {v.label}
              {n ? <span className={`fws-chip ${v.id === "emr_failed" ? "fws-tone-bad" : "fws-tone-warn"}`}>{n}</span> : null}
            </button>
          );
        })}
      </div>

      <form
        className="fws-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(qInput);
          setPage(1);
        }}
      >
        <div className="fws-field">
          <label className="fws-label" htmlFor="fws-from">Submitted from</label>
          <input id="fws-from" type="date" className="fws-input" value={from} max={to || undefined}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        </div>
        <div className="fws-field">
          <label className="fws-label" htmlFor="fws-to">to</label>
          <input id="fws-to" type="date" className="fws-input" value={to} min={from || undefined}
            onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        </div>
        <div className="fws-field" style={{ minWidth: 220, flex: "1 1 220px", maxWidth: 360 }}>
          <label className="fws-label" htmlFor="fws-q">Patient</label>
          <input id="fws-q" className="fws-input" placeholder="Name or patient ID" value={qInput} onChange={(e) => setQInput(e.target.value)} />
        </div>
        <button type="submit" className="fws-btn fws-btn-primary">Search</button>
        {(from || to || q) && (
          <button type="button" className="fws-btn fws-btn-ghost" onClick={() => { setFrom(""); setTo(""); setQ(""); setQInput(""); setPage(1); }}>
            Clear
          </button>
        )}
        <span className="fws-spacer" />
        <button type="button" className="fws-btn" onClick={() => { load(); loadCounts(); }} disabled={loading}>Refresh</button>
      </form>

      {error && <div className="fws-alert fws-alert-error" role="alert" style={{ marginBottom: 12 }}>{error}</div>}
      {view === "staff" && staffNote && <div className="fws-alert fws-alert-info" style={{ marginBottom: 12 }} data-testid="fwi-staff-unavailable">{staffNote}</div>}
      {view === "staff" && !staffNote && (
        <DraftsStrip
          version={draftsVersion}
          onContinue={(it) => openStaffForm(null, { submissionId: it.id, onDone: () => { load(); setDraftsVersion((n) => n + 1); } })}
        />
      )}

      <div className="fws-table-wrap">
        <table className="fws-table">
          <thead>
            <tr>
              <th>Submitted</th>
              <th>Patient</th>
              <th>Form</th>
              <th>Lang</th>
              <th>Score</th>
              <th>Flags</th>
              <th>Review</th>
              <th>EMR</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((it) => {
              const p = it.patient || {};
              const [emrLabel, emrTone] = statusInfo(EMR_STATUS, emrStatusOf(it));
              return (
                <tr
                  key={it.id}
                  tabIndex={0}
                  data-testid={`inbox-row-${it.id}`}
                  onClick={() => setReviewId(it.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setReviewId(it.id);
                  }}
                >
                  <td className="fws-nowrap">{fmtDateTime(it.submitted_at)}</td>
                  <td>
                    <div className="fws-strong">{[p.firstname, p.lastname].filter(Boolean).join(" ") || `Patient ${it.patientid || p.patientid || ""}`}</div>
                    <div className="fws-small fws-muted">{p.dob ? `DOB ${fmtDate(p.dob)}` : ""}{p.patientid || it.patientid ? ` · ID ${p.patientid || it.patientid}` : ""}</div>
                  </td>
                  <td>
                    <div>
                      {it.form_title || it.form_id}
                      {it.channel === "internal" && <span className="fws-chip fws-tone-info" style={{ marginLeft: 6 }} data-testid={`staff-chip-${it.id}`}>Staff</span>}
                    </div>
                    {it.created_by && <div className="fws-small fws-muted">{it.channel === "internal" ? "Filled by" : "Sent by"} {it.created_by}</div>}
                  </td>
                  <td>{(it.language || "en").toUpperCase()}</td>
                  <td><ScoreChip item={it} sev={sevByItem[it.id]} /></td>
                  <td><FlagsCell item={it} /></td>
                  <td>
                    {it.channel === "internal"
                      ? <span className="fws-chip fws-tone-ok" title={it.reviewed_by ? `saved by ${it.reviewed_by}` : ""}>Staff form</span>
                      : it.review_status === "reviewed"
                        ? <span className="fws-chip fws-tone-ok" title={it.reviewed_by ? `by ${it.reviewed_by}` : ""}>Reviewed</span>
                        : <span className="fws-chip fws-tone-warn">Unreviewed</span>}
                  </td>
                  <td>
                    <span className={`fws-chip fws-tone-${emrTone}`} title={it.emr_error || ""}>{emrLabel}</span>
                  </td>
                </tr>
              );
            })}
            {!loading && list.items.length === 0 && (
              <tr><td colSpan={8} className="fws-empty">Nothing here.</td></tr>
            )}
            {loading && list.items.length === 0 && (
              <tr><td colSpan={8} className="fws-empty">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="fws-pager">
        <span className="fws-small fws-muted">{list.total ? `${firstRow}–${lastRow} of ${list.total}` : ""}</span>
        <button type="button" className="fws-btn fws-btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((n) => Math.max(1, n - 1))}>Previous</button>
        <button type="button" className="fws-btn fws-btn-sm" disabled={page >= pages || loading} onClick={() => setPage((n) => n + 1)}>Next</button>
      </div>

      {reviewId && (
        <SubmissionReview
          submissionId={reviewId}
          onClose={() => setReviewId(null)}
          onChanged={() => {
            load();
            loadCounts();
          }}
        />
      )}
      {wizard}
      {staffFormFill}
    </div>
  );
}

export default function FormInboxPage() {
  if (!practiceFeatures().formWizard) return <Unavailable />;
  return (
    <SideNavLayout>
      <FormInbox />
    </SideNavLayout>
  );
}
